import React, { createContext, useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { colors, fonts, shadows } from "../theme/theme";

const ThemeContext = createContext();

const nirvaGradient = `linear-gradient(
  90deg,
  #e6ebf0 0%,
  rgba(232, 231, 234, 0.67) 5%,
  rgba(253, 254, 253, 1) 30%,
  rgba(253, 254, 253, 0.28) 50%,
  rgba(253, 254, 253, 1) 70%,
  rgba(232, 231, 234, 0.67) 95%,
  #e6ebf0 100%
)`;

export const ThemeProviderComponent = ({ children }) => {
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: "light",
          primary: {
            main: colors.secondary, // #29405A navy - for MUI primary (buttons, etc.)
          },
          background: {
            default: colors.background, // #F5F3ED
            paper: colors.cardBg, // #FFFFFF
            gradient: nirvaGradient,
            backgroundColor: colors.background,
          },
          text: {
            primary: colors.primary, // #33261D
            secondary: colors.textLight, // #666
          },
          button: {
            primary: colors.secondary, // #29405A
            secondary: "transparent",
            primaryHover: "#1f2e3e",
          },
          border: `1px solid ${colors.lightBorder}`,
          action: {
            hover: colors.hoverBg,
            selected: "rgba(41, 64, 90, 0.08)",
          },
        },

        // === Typography (Gotham headings, Quicksand body) ===
        typography: {
          fontFamily: fonts.quicksand,
          h1: {
            fontFamily: fonts.gotham,
            fontWeight: 500,
            color: colors.primary,
            textTransform: "uppercase",
            letterSpacing: "0.2px",
          },
          h2: {
            fontFamily: fonts.gotham,
            fontWeight: 500,
            color: colors.primary,
            textTransform: "uppercase",
            letterSpacing: "0.2px",
          },
          h3: {
            fontFamily: fonts.gotham,
            fontWeight: 500,
            color: colors.primary,
            letterSpacing: "0.2px",
          },
          h4: {
            fontFamily: fonts.gotham,
            fontWeight: 400,
            color: colors.primary,
          },
          h5: {
            fontFamily: fonts.gotham,
            fontWeight: 400,
            color: colors.primary,
          },
          h6: {
            fontFamily: fonts.gotham,
            fontWeight: 400,
            color: colors.primary,
          },
          body1: {
            fontFamily: fonts.quicksand,
            fontWeight: 400,
            color: colors.primary,
          },
          body2: {
            fontFamily: fonts.quicksand,
            fontWeight: 400,
            color: colors.textLight,
          },
          button: {
            fontFamily: fonts.quicksand,
            fontWeight: 600,
            textTransform: "none",
          },
          caption: {
            fontFamily: fonts.quicksand,
            fontWeight: 400,
            color: colors.textLight,
          },
        },

        // === Custom theme properties (consumed by dashboard components) ===
        heading: {
          fontSize: "40px",
          fontFamily: fonts.gotham,
          fontWeight: 500,
          color: colors.primary,
          textTransform: "uppercase",
          letterSpacing: "0.2px",
          lineHeight: 1.2,
        },
        heading2: {
          fontSize: "24px",
          fontFamily: fonts.gotham,
          fontWeight: 500,
          color: colors.primary,
          letterSpacing: "0.2px",
          lineHeight: 1.3,
        },
        body: {
          color: colors.primary,
          fontFamily: fonts.quicksand,
          fontSize: "18px",
          fontWeight: 400,
          lineHeight: 1.6,
        },
        body2: {
          color: colors.textLight,
          fontFamily: fonts.quicksand,
          fontSize: "16px",
          fontWeight: 400,
          lineHeight: 1.5,
        },

        // === Card & shadow tokens ===
        card: {
          background: colors.cardBg,
          borderRadius: "30px",
          shadow: shadows.card,
          hoverShadow: shadows.cardHover,
        },
        shadowTokens: {
          card: shadows.card,
          cardHover: shadows.cardHover,
          soft: shadows.soft,
          modal: shadows.modal,
        },

        // === Component overrides ===
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                fontFamily: fonts.quicksand,
                fontWeight: 500,
                textTransform: "none",
                borderRadius: "10px",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              },
              contained: {
                backgroundColor: colors.secondary,
                color: colors.white,
                "&:hover": {
                  backgroundColor: "#1f2e3e",
                },
              },
              outlined: {
                borderColor: colors.secondary,
                color: colors.secondary,
                "&:hover": {
                  borderColor: colors.secondary,
                  backgroundColor: "rgba(41, 64, 90, 0.04)",
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: "16px",
              },
            },
          },
        },
      }),
    []
  );

  // Keep switchTheme as a no-op for backward compatibility
  const switchTheme = () => {};

  return (
    <ThemeContext.Provider value={{ selectedTheme: "default", switchTheme }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
